// The AE-half reviewer profile, rendered compactly outside the Details page:
//  - under a preprint in reviewer-finding, read-only for students (July-2 FR-J11), and
//  - behind the per-preprint "Reviewer profile" button in Quality Check, read + RBAC-gated
//    edit so checkers know the target and can adjust it (FR-J16).
// Headings match the pitch-deck Reviewer Profile prompts (Angel wording).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SelectedView } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Button, Field, Spinner, Textarea, Input } from "../../components/ui.js";
import { RichText } from "../../components/RichText.js";

const PROFILE_FIELDS = [
  {
    key: "Reviewer Profile(discipline)",
    label: "What training / education should a reviewer have? (e.g. epidemiology)",
  },
  {
    key: "Reviewer Profile(method expertise)",
    label: "Please describe the minimum reviewer profile (describe the type of research they conduct)",
  },
  {
    key: "Reviewer Profile(topical expertise)",
    label: "Please list additional desired (but not required) reviewer characteristics (methods and/or research topics)",
  },
] as const;

export function ReviewerProfilePanel({ selectedId, canEdit = false }: { selectedId: string; canEdit?: boolean }): JSX.Element {
  const qc = useQueryClient();
  const { data: s, isLoading } = useQuery({
    queryKey: qk.selectedOne(selectedId),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${selectedId}`),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirty = Object.keys(draft).length > 0;

  const save = useMutation({
    mutationFn: () => api.patch<SelectedView>(`/preprints/selected/${selectedId}`, { pitch: draft }),
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(selectedId), updated);
      setDraft({});
    },
  });

  if (isLoading) return <Spinner label="Loading reviewer profile…" />;
  if (!s) return <p className="text-xs text-slate-500">Reviewer profile unavailable.</p>;

  const pitch = s.pitch as Record<string, unknown>;
  const valueOf = (key: string): string => (key in draft ? draft[key]! : String(pitch[key] ?? ""));

  return (
    <div className="space-y-3">
      {PROFILE_FIELDS.map(({ key, label }) =>
        canEdit ? (
          <Field key={key} label={label}>
            {key === "Reviewer Profile(method expertise)" ? (
              <Textarea value={valueOf(key)} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
            ) : (
              <Input value={valueOf(key)} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
            )}
          </Field>
        ) : (
          <div key={key}>
            <span className="text-xs font-medium text-slate-600">{label}</span>
            <RichText>{String(pitch[key] ?? "")}</RichText>
          </div>
        ),
      )}

      {s.modelReviewers.length > 0 && (
        <div>
          <span className="text-xs font-medium text-slate-600">Model Peer Reviewers</span>
          <ul className="mt-1 space-y-1">
            {s.modelReviewers.map((r) => (
              <li key={r.slot} className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="slate">#{r.slot}</Badge>
                <span className="font-medium">{r.name || "—"}</span>
                {r.profileUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">Profile ↗</a>
                )}
                {r.pubmedUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">PubMed ↗</a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* "Most Similar Articles" (Prophy) removed — not live (July-7 FR-K7). */}

      {canEdit && (
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : dirty ? "Save profile" : "Saved"}
        </Button>
      )}
    </div>
  );
}
